import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { BriefcaseBusiness, Building2, FolderKanban, Plus, RefreshCw, Save, Search, Trash2, Users } from "lucide-react";
import { useUser } from "./Context/UserContext.tsx";
import { useProjects } from "./Context/ProjectContext.tsx";
import "./CSS/Management.css";

const API_BASE = "https://timetrakerapi.sntservices.in";
interface ClientOption { id: number; name: string; }
interface StaffMember { email: string; firstName?: string | null; projectId?: string | null; }
const assignedTo = (person: StaffMember, projectId: number) =>
  (person.projectId || "").split(",").map((id) => Number(id.trim())).includes(projectId);

const Project: React.FC = () => {
  const { user } = useUser();
  const adminId = user?.id;
  const { projects, setProjects } = useProjects();
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [clientId, setClientId] = useState("");
  const [newName, setNewName] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");

  const loadData = useCallback(async (signal?: AbortSignal) => {
    if (!adminId) return;
    const getList = async <T,>(path: string): Promise<T[]> => {
      try {
        const response = await axios.get<T[]>(`${API_BASE}/${path}`, { params: { Adminid: adminId }, signal });
        return Array.isArray(response.data) ? response.data : [];
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) return [];
        throw err;
      }
    };
    try {
      const [projectRows, clientRows, staffRows] = await Promise.all([
        getList<{ id: number; name: string; client: number }>("GetProject"),
        getList<ClientOption>("GetClient"),
        getList<StaffMember>("GetEmployeeDetails"),
      ]);
      if (signal?.aborted) return;
      setProjects(projectRows);
      setClients(clientRows);
      setStaff(staffRows);
      setError("");
    } catch (err) {
      if (!axios.isCancel(err)) setError("Projects could not be loaded. Try refreshing the page.");
    }
  }, [adminId, setProjects]);

  useEffect(() => {
    if (!adminId) { setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    loadData(controller.signal).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [adminId, loadData]);

  const selected = projects.find((project) => project.id === selectedId);
  const filtered = useMemo(() => projects.filter((project) =>
    project.name.toLowerCase().includes(search.trim().toLowerCase())
  ), [projects, search]);
  const projectMembers = selected ? staff.filter((person) => assignedTo(person, selected.id)) : [];
  const linkedClient = clients.find((client) => client.id === selected?.client);

  const selectProject = (project: typeof projects[number]) => {
    setSelectedId(project.id);
    setClientId(project.client ? String(project.client) : "");
    setError("");
  };

  const addProject = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) { setError("Enter a project name."); return; }
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API_BASE}/AddProject`, null, { params: { Adminid: adminId, projectName: name } });
      setNewName("");
      await loadData();
      toast.success("Project added successfully");
    } catch { setError("Project could not be added. Check whether that name already exists."); }
    finally { setSaving(false); }
  };

  const saveClient = async () => {
    if (!selected || !clientId) { setError("Choose a client for this project."); return; }
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API_BASE}/AddclientProject`, null, { params: { Adminid: adminId, projectid: selected.id, clientid: clientId } });
      await loadData();
      toast.success("Project client updated");
    } catch { setError("The project client could not be saved."); }
    finally { setSaving(false); }
  };

  const deleteProject = async () => {
    if (!selected) return;
    setDeleting(true);
    setError("");
    try {
      await axios.delete(`${API_BASE}/deleteProject`, { params: { Adminid: adminId, projectid: selected.id } });
      setSelectedId(null);
      setClientId("");
      setConfirmDelete(false);
      await loadData();
      toast.success("Project deleted successfully");
    } catch { setError("Project could not be deleted. Please try again."); }
    finally { setDeleting(false); }
  };

  return <main className="management-page">
    <header className="management-header"><div><span className="management-eyebrow">Workspace / Projects</span><h1>Projects</h1><p>Organize work by project, client, and assigned team members.</p></div><div className="management-header-actions"><button className="management-button" type="button" onClick={() => loadData()}><RefreshCw size={15} /> Refresh</button><Link className="management-button management-button-primary" to="/admin-dashboard/client"><Building2 size={15} /> View clients</Link></div></header>
    {error && <div className="management-alert" role="alert">{error} <button type="button" onClick={() => setError("")}>Dismiss</button></div>}
    <section className="management-metrics" aria-label="Project overview"><div className="management-metric"><span><FolderKanban size={16} /> Total projects</span><strong>{projects.length}</strong></div><div className="management-metric"><span><Building2 size={16} /> Linked to clients</span><strong>{projects.filter((project) => !!project.client).length}</strong></div><div className="management-metric"><span><Users size={16} /> Team members</span><strong>{staff.length}</strong></div></section>
    <div className="management-layout">
      <section className="management-panel"><div className="management-panel-heading"><div><h2>Project directory</h2><small>{projects.length} projects</small></div></div><form className="management-create" onSubmit={addProject}><input className="management-input" aria-label="New project name" placeholder="New project name" value={newName} onChange={(event) => setNewName(event.target.value)} /><button className="management-button management-button-primary" disabled={saving || !newName.trim()}><Plus size={15} /> {saving ? "Adding…" : "Add"}</button></form><label className="management-search"><Search size={15} /><input className="management-input" aria-label="Search projects" placeholder="Search projects" value={search} onChange={(event) => setSearch(event.target.value)} /></label><div className="management-list">{loading ? <p className="management-list-empty">Loading projects…</p> : filtered.length ? filtered.map((project) => <button type="button" key={project.id} className={`management-list-item${selectedId === project.id ? " is-active" : ""}`} onClick={() => selectProject(project)}><span className="management-list-icon"><FolderKanban size={17} /></span><span className="management-list-copy"><strong>{project.name}</strong><small>{clients.find((client) => client.id === project.client)?.name || "No client linked"}</small></span></button>) : <p className="management-list-empty">{search ? "No projects match your search." : "No projects yet. Add your first project above."}</p>}</div></section>
      <section className="management-panel management-detail">{selected ? <><div className="management-detail-header"><div><span className="management-eyebrow">Project details</span><h2>{selected.name}</h2><p>Project ID #{selected.id} · {projectMembers.length} team member{projectMembers.length === 1 ? "" : "s"}</p></div><button className="management-button management-button-danger" type="button" onClick={() => setConfirmDelete(true)}><Trash2 size={15} /> Delete project</button></div><div className="management-detail-body"><h3 className="management-section-title">Client</h3><p className="management-section-copy">Choose the client associated with this project.</p><div className="management-info-card" style={{ marginBottom: 17 }}><Building2 size={20} /><div><strong>{linkedClient?.name || "No client linked"}</strong><small>Current client</small></div></div><div className="management-actions"><label className="management-field" style={{ minWidth: 220, flex: 1 }}>Assign a client<select className="management-select" value={clientId} onChange={(event) => setClientId(event.target.value)}><option value="">Select a client</option>{clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label><button className="management-button management-button-primary" type="button" disabled={saving || !clientId} onClick={saveClient}><Save size={15} /> {saving ? "Saving…" : "Save client"}</button></div><div className="management-panel-divider" style={{ margin: "25px 0" }} /><h3 className="management-section-title">Project members <span className="management-chip">{projectMembers.length}</span></h3><p className="management-section-copy">Team members currently assigned to this project. Manage assignments on the Team page.</p>{projectMembers.length ? <div className="management-member-grid">{projectMembers.map((person) => <div className="management-member" key={person.email}><Users size={17} /><span><strong>{person.firstName || person.email}</strong><small>{person.email}</small></span></div>)}</div> : <div className="management-info-card"><Users size={20} /><div><strong>No members assigned</strong><small>Assign staff from the Team page.</small></div></div>}<div style={{ marginTop: 17 }}><Link className="management-button" to="/admin-dashboard/team"><Users size={15} /> Manage team</Link></div></div></> : <div className="management-detail-empty"><BriefcaseBusiness size={36} /><h2>Select a project</h2><p>Choose a project to review its client and team members.</p></div>}</section>
    </div>
    <Modal show={confirmDelete} onHide={() => setConfirmDelete(false)} centered><Modal.Header closeButton><Modal.Title>Delete project?</Modal.Title></Modal.Header><Modal.Body>This will delete {selected?.name}. This action cannot be undone.</Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setConfirmDelete(false)}>Cancel</Button><Button variant="danger" disabled={deleting} onClick={deleteProject}>{deleting ? "Deleting…" : "Delete project"}</Button></Modal.Footer></Modal>
  </main>;
};

export default Project;
